import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR, STATUS_STYLE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowUpRight, ShoppingCart, ClipboardCheck, Truck, TrendingUp, Landmark, AlertTriangle } from "lucide-react";
import DealerDashboard from "./DealerDashboard";

const KPI = ({ label, value, icon: Icon, hint }) => (
  <Card className="p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
        <div className="mt-2 text-2xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>{value}</div>
        {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      </div>
      <div className="p-2 rounded-md bg-blue-50 text-blue-700"><Icon size={18} strokeWidth={1.5} /></div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => { 
    if (user?.role !== "dealer") {
      api.get("/dashboard/stats").then(({ data }) => setStats(data)); 
    }
  }, [user?.role]);

  // If user is a Dealer, show dedicated Dealer Dashboard
  if (user?.role === "dealer") {
    return <DealerDashboard />;
  }

  if (!stats) return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_,i) => <Skeleton key={i} className="h-28" />)}
      </div>
      <Skeleton className="h-80" />
    </div>
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Overview</div>
        <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>
          Good day, {user?.name?.split(" ")[0]}
        </h1>
        <p className="text-sm text-slate-600 mt-1">Here's what's moving through your pipeline.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total POs" value={stats.total_po} icon={ShoppingCart} hint={`${stats.submitted} pending`} />
        <KPI label="Approved" value={stats.approved} icon={ClipboardCheck} />
        <KPI label="SOs raised" value={stats.total_so} icon={Truck} hint={`${stats.fulfilled} fulfilled`} />
        <KPI label="Committed value" value={fmtINR(stats.revenue)} icon={TrendingUp} />
      </div>

      {stats.payments && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Cash flow &middot; Bank Transfers</div>
              <h2 className="text-xl font-medium tracking-tight mt-1" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Payment recovery</h2>
            </div>
            <Link to="/payments" className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="link-view-payments">
              Go to payments <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-5" data-testid="kpi-committed">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Committed</div>
                  <div className="mt-2 text-2xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>{fmtINR(stats.payments.committed)}</div>
                </div>
                <div className="p-2 rounded-md bg-slate-100 text-slate-700"><TrendingUp size={18} strokeWidth={1.5} /></div>
              </div>
            </Card>
            <Card className="p-5" data-testid="kpi-paid">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Paid (verified)</div>
                  <div className="mt-2 text-2xl font-medium text-emerald-700" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>{fmtINR(stats.payments.paid_verified)}</div>
                </div>
                <div className="p-2 rounded-md bg-emerald-50 text-emerald-700"><Landmark size={18} strokeWidth={1.5} /></div>
              </div>
            </Card>
            <Card className="p-5" data-testid="kpi-pending">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Awaiting review</div>
                  <div className="mt-2 text-2xl font-medium text-amber-700" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>{fmtINR(stats.payments.paid_pending)}</div>
                </div>
                <div className="p-2 rounded-md bg-amber-50 text-amber-700"><Landmark size={18} strokeWidth={1.5} /></div>
              </div>
            </Card>
            <Card className="p-5" data-testid="kpi-outstanding">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Outstanding</div>
                  <div className={`mt-2 text-2xl font-medium ${stats.payments.outstanding > 0 ? "text-rose-700" : "text-slate-500"}`} style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>{fmtINR(stats.payments.outstanding)}</div>
                </div>
                <div className="p-2 rounded-md bg-rose-50 text-rose-700"><AlertTriangle size={18} strokeWidth={1.5} /></div>
              </div>
            </Card>
          </div>

          {stats.payments.top_outstanding?.length > 0 && (
            <Card className="mt-4 p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-3">Top outstanding by PO</div>
              <div className="divide-y divide-slate-100">
                {stats.payments.top_outstanding.map(row => (
                  <Link to={`/po/${row.po_id}`} key={row.po_id} className="flex items-center justify-between py-2.5 hover:bg-slate-50 rounded-md px-2 -mx-2 transition-colors" data-testid={`outstanding-${row.po_number}`}>
                    <div>
                      <div className="text-sm font-mono text-slate-700">{row.po_number}</div>
                      <div className="text-xs text-slate-500">{row.dealer_name} · {row.dealer_area}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-rose-700">{fmtINR(row.outstanding)}</div>
                      <div className="text-[10px] text-slate-500">of {fmtINR(row.total)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Recent Purchase Orders</div>
              <h2 className="text-xl font-medium tracking-tight mt-1" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Latest activity</h2>
            </div>
            <Link to="/po" className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="link-view-all-po">
              View all <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {(!stats.recent || stats.recent.length === 0) && <div className="py-10 text-center text-sm text-slate-500">No POs yet. Head to Catalogue to create one.</div>}
            {(stats.recent || []).map(po => {
              const s = STATUS_STYLE[po.status] || STATUS_STYLE.draft;
              return (
                <Link to={`/po/${po.po_id}`} key={po.po_id} className="flex items-center justify-between py-3 hover:bg-slate-50 rounded-md transition-colors px-2 -mx-2">
                  <div>
                    <div className="text-sm font-medium">{po.po_number}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{po.dealer_name} · {po.dealer_area}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-sm font-medium">{fmtINR(po.total)}</div>
                    <Badge variant="outline" className={`${s.bg} ${s.text} ${s.border}`}>{s.label}</Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Top moving SKUs</div>
          <h2 className="text-xl font-medium tracking-tight mt-1 mb-4" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Demand leaders</h2>
          <div className="space-y-3">
            {(!stats.top || stats.top.length === 0) && <div className="py-10 text-center text-sm text-slate-500">No volume data yet</div>}
            {(stats.top || []).map((t, idx) => (
              <div key={t.sku || idx} className="flex items-center justify-between text-sm py-1.5">
                <div className="flex-1 pr-4">
                  <div className="font-medium text-xs truncate max-w-[200px]" title={t.name}>{t.name}</div>
                  <div className="text-[10px] text-slate-400 font-mono">{t.sku}</div>
                </div>
                <span className="font-semibold text-xs text-slate-900 bg-slate-100 px-2 py-0.5 rounded">{t.qty} units</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
